/**
 * Binding & override data layer for the top-level Scribe surface (LF-3712,
 * FE-8): create-matter-from-recording, associate / unbind, the matter-candidate
 * picker, treat-as-probate, dismiss/restore-suggestion, archive/unarchive, and
 * cancel-processing.
 *
 * Co-locates the wire types, directApiClient actions, and the React Query hooks
 * — every mutation invalidates the recording detail + all recording lists so
 * the open session page and the home list both refresh.
 */

'use client';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { directApiClient, DirectApiError } from '@/lib/api/direct-client';
import { recordingKeys } from '@/lib/query-keys';
import { SMART_CACHE_CONFIG } from '../query-config';
import type { ApplyChangesRequest, Recording } from './types';

const RECORDINGS_PATH = '/recordings';

// ─── Wire types ─────────────────────────────────────────────────────

export interface MatterCandidateOverlap {
  reason: string;
  /** 0–1 overlap score. */
  score: number;
}

export interface MatterCandidate {
  matterId: number;
  reference: string | null;
  /**
   * How to name the matter in the picker — render this. `Estate of <deceased>`
   * for an estate matter, the clients' names for an estate-planning one
   * (VALE-450).
   */
  subjectLabel: string;
  /**
   * The deceased label specifically, and empty when the matter has no deceased
   * (a planning matter never does). Kept honest rather than repurposed — render
   * {@link subjectLabel}.
   *
   * @deprecated Use `subjectLabel`.
   */
  deceasedLabel: string;
  executorLabel: string | null;
  stage: string | null;
  /** Present (non-null) for the "likely match" group; null for the rest. */
  overlap: MatterCandidateOverlap | null;
}

/**
 * Minimum overlap score (0–1) worth surfacing in the associate/import matter
 * pickers. Below this, the match is too weak to be signal: the matter stays
 * searchable but is not ranked as a "likely match" and its "N% overlap" badge
 * is hidden — a 13% / 20% / 47% overlap pill is noise, not a useful cue. Shared
 * by both the AI Scribe and top-level Intake pickers so the bar stays in one
 * place.
 */
export const STRONG_OVERLAP_THRESHOLD = 0.8;

/**
 * True when a candidate's overlap is strong enough to rank as a likely match
 * and show its overlap badge (score ≥ {@link STRONG_OVERLAP_THRESHOLD}).
 */
export function isStrongOverlap(
  candidate: Pick<MatterCandidate, 'overlap'>
): boolean {
  return (candidate.overlap?.score ?? 0) >= STRONG_OVERLAP_THRESHOLD;
}

export interface ConfirmedDeceasedName {
  firstName: string;
  lastName: string;
}

/**
 * Create-matter payload — the apply-changes payload plus an optional
 * `confirmedDeceasedName`, required only when extraction produced no deceased
 * (`422 DECEASED_NAME_REQUIRED`).
 */
export interface CreateMatterFromRecordingRequest extends ApplyChangesRequest {
  confirmedDeceasedName?: ConfirmedDeceasedName;
  /**
   * Matter type to create (VALE-222), from the orphan banner's picker when the
   * classifier could not place the recording. Omitted, the server derives it as
   * before: a planning extraction creates an estate-planning matter, a probate
   * verdict an estate-administration one.
   */
  matterType?: string;
}

export interface CreateMatterFromRecordingResponse {
  recordingId: number;
  matterId: number;
  matterReference: string;
  deceasedLabel: string;
  applied: {
    contacts: { created: number; updated: number };
    inventoryItems: { created: number; updated: number };
    checklistItems: { added: number };
    existingItemNotes: { applied: number };
    matter: { updated: boolean };
    entitlements?: { created: number };
    /** Accepted proposed handovers (VALE-1866); absent when none were accepted. */
    handovers?: import('@/lib/services/matter-handling/types').HandoversApplyOutcome;
    classResolutions?: { applied: number };
  };
}

/** Coded error codes the binding flows switch on (FE contract error table). */
export type RecordingBindingErrorCode =
  | 'RECORDING_NOT_READY'
  | 'RECORDING_ALREADY_BOUND'
  | 'RECORDING_NOT_PROBATE'
  | 'BINDING_LOCKED'
  | 'DECEASED_NAME_REQUIRED'
  | 'FIRM_JURISDICTION_MISSING'
  | 'MATTER_NOT_FOUND'
  | 'PDF_SERVICE_UNAVAILABLE'
  | 'AUDIO_UNAVAILABLE';

/** Extract the BE error `code` from a thrown DirectApiError, if present. */
export function recordingErrorCode(
  error: unknown
): RecordingBindingErrorCode | undefined {
  if (error instanceof DirectApiError) {
    const code = (error.response as { code?: string } | undefined)?.code;
    return code as RecordingBindingErrorCode | undefined;
  }
  return undefined;
}

// ─── Actions ────────────────────────────────────────────────────────

export function createMatterFromRecording(
  recordingId: number,
  body: CreateMatterFromRecordingRequest
): Promise<CreateMatterFromRecordingResponse> {
  return directApiClient.post<CreateMatterFromRecordingResponse>(
    `${RECORDINGS_PATH}/${recordingId}/create-matter`,
    body
  );
}

export function getMatterCandidates(
  recordingId: number,
  q?: string,
  limit?: number
): Promise<MatterCandidate[]> {
  return directApiClient.get<MatterCandidate[]>(
    `${RECORDINGS_PATH}/${recordingId}/matter-candidates`,
    { params: { q, limit } }
  );
}

export function associateRecording(
  recordingId: number,
  matterId: number
): Promise<Recording> {
  return directApiClient.post<Recording>(
    `${RECORDINGS_PATH}/${recordingId}/associate`,
    { matterId }
  );
}

export function unbindRecording(recordingId: number): Promise<Recording> {
  return directApiClient.post<Recording>(
    `${RECORDINGS_PATH}/${recordingId}/unbind`,
    {}
  );
}

export function treatAsProbate(recordingId: number): Promise<Recording> {
  return directApiClient.post<Recording>(
    `${RECORDINGS_PATH}/${recordingId}/treat-as-probate`,
    {}
  );
}

export function dismissSuggestion(recordingId: number): Promise<Recording> {
  return directApiClient.post<Recording>(
    `${RECORDINGS_PATH}/${recordingId}/dismiss-suggestion`,
    {}
  );
}

export function restoreSuggestion(recordingId: number): Promise<Recording> {
  return directApiClient.post<Recording>(
    `${RECORDINGS_PATH}/${recordingId}/restore-suggestion`,
    {}
  );
}

export function archiveRecording(recordingId: number): Promise<Recording> {
  return directApiClient.post<Recording>(
    `${RECORDINGS_PATH}/${recordingId}/archive`,
    {}
  );
}

export function unarchiveRecording(recordingId: number): Promise<Recording> {
  return directApiClient.post<Recording>(
    `${RECORDINGS_PATH}/${recordingId}/unarchive`,
    {}
  );
}

export function cancelRecording(
  recordingId: number
): Promise<Recording | { deleted: true }> {
  return directApiClient.post<Recording | { deleted: true }>(
    `${RECORDINGS_PATH}/${recordingId}/cancel`,
    {}
  );
}

// ─── Hooks ──────────────────────────────────────────────────────────

function invalidate(
  queryClient: ReturnType<typeof useQueryClient>,
  recordingId: number
) {
  queryClient.invalidateQueries({
    queryKey: recordingKeys.detail(recordingId)
  });
  queryClient.invalidateQueries({ queryKey: recordingKeys.lists() });
}

/**
 * Ranked matter candidates for the associate picker. `q` filters server-side
 * (deceased / executor / reference); rows with `overlap` are the likely-match
 * group. Only fetched while `enabled` (the picker is open).
 */
export function useMatterCandidates(
  recordingId: number,
  q: string,
  enabled: boolean
) {
  return useQuery({
    queryKey: [...recordingKeys.detail(recordingId), 'matter-candidates', q],
    queryFn: () => getMatterCandidates(recordingId, q.trim() || undefined),
    enabled,
    ...SMART_CACHE_CONFIG
  });
}

export function useCreateMatterFromRecording(recordingId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateMatterFromRecordingRequest) =>
      createMatterFromRecording(recordingId, body),
    onSuccess: () => invalidate(queryClient, recordingId)
  });
}

export function useAssociateRecording(recordingId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (matterId: number) => associateRecording(recordingId, matterId),
    onSuccess: () => invalidate(queryClient, recordingId)
  });
}

export function useUnbindRecording(recordingId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => unbindRecording(recordingId),
    onSuccess: () => invalidate(queryClient, recordingId)
  });
}

export function useTreatAsProbate(recordingId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => treatAsProbate(recordingId),
    onSuccess: () => invalidate(queryClient, recordingId)
  });
}

export function useDismissSuggestion(recordingId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => dismissSuggestion(recordingId),
    onSuccess: () => invalidate(queryClient, recordingId)
  });
}

export function useRestoreSuggestion(recordingId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => restoreSuggestion(recordingId),
    onSuccess: () => invalidate(queryClient, recordingId)
  });
}

export function useArchiveRecording(recordingId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => archiveRecording(recordingId),
    onSuccess: () => invalidate(queryClient, recordingId)
  });
}

export function useUnarchiveRecording(recordingId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => unarchiveRecording(recordingId),
    onSuccess: () => invalidate(queryClient, recordingId)
  });
}

export function useCancelRecording(recordingId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => cancelRecording(recordingId),
    onSuccess: () => invalidate(queryClient, recordingId)
  });
}
